import React from 'react';

import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './loading.component.module.styl';

import type {ComponentProps} from 'react';


/**
 * Visual style variants for the `Loading` spinner.
 *
 * - `NONE` — default full-sized spinner, suitable for page or section loaders.
 * - `ICON` — compact spinner sized to fit inside icon-sized spaces (e.g. next to a label).
 * - `FIELD_ICON` — inline spinner sized for form fields, appears inside an input or button.
 */
export const Types = {
    NONE: 'NONE',
    ICON: 'ICON',
    FIELD_ICON: 'FIELD_ICON',
} as const;

const classNames = withCssModule(styles);

type LoadingType = keyof typeof Types;

type NativeSpanProps = ComponentProps<'span'>;

export interface Props extends NativeSpanProps {
    /**
     * When `true`, the spinner appears immediately without the built-in entrance
     * animation delay. Use this when you want instant visual feedback (e.g. for
     * button loading states) rather than the default short delay that prevents
     * flashing on fast operations. Defaults to `false`.
     */
    fast?: boolean,

    /**
     * Visual size and style variant. Accepts a key from the `Types` object:
     * - `Types.NONE` (default) — full-sized spinner for page or section loaders.
     * - `Types.ICON` — compact spinner that fits inside icon-sized spaces.
     * - `Types.FIELD_ICON` — slim inline spinner for form fields or buttons.
     */
    type?: LoadingType,

    /**
     * Custom classes
     * @deprecated It will be removed in 6 months 09-20-2025. use className instead
     */
    mix?: string,

    /**
     * Apply dark mode styles
     */
    darkMode?: boolean,
}

/**
 * Animated loading spinner rendered as a `<span>` with `role="progressbar"`.
 *
 * Three visual styles are available via the `type` prop: the default spinner
 * (`NONE`), a compact icon-sized spinner (`ICON`), and an inline field-sized
 * spinner (`FIELD_ICON`). Set `fast` to skip the entrance animation delay for
 * immediate feedback. Wrap with `LoadingHolder` to reserve layout space and
 * prevent content shift.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/components-loading-loading}
 */
export function Loading(props: Props) {
    const {
        className,
        fast = false,
        mix,
        darkMode = false,
        role = 'progressbar',
        type = Types.NONE,
        ...restProps
    } = props;

    const loadingProps = {
        'aria-label': 'Loading',
        ...restProps,
        role,
        'className': classNames('loading', mix, className, {
            'loading_fast': fast,
            'loading_icon': type === Types.ICON,
            'loading_field-icon': type === Types.FIELD_ICON,
            'loading_dark-mode': darkMode,
        }),
    };

    return <span {...loadingProps}/>;
}
